import "@/App.css";
import { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { APIProvider } from "@vis.gl/react-google-maps";
import { AuthProvider } from "@/context/AuthContext";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import RouteFallback from "@/components/RouteFallback";
import { lazyWithPreload } from "@/lib/lazyWithPreload";
import Login from "@/pages/Login";

// Route-based code splitting: every page below ships as its own chunk and is
// fetched on demand, shrinking the initial bundle. Login + the shell stay eager
// so the first paint and the app frame are instant.
const Register = lazyWithPreload(() => import("@/pages/Register"));
const ForgotPassword = lazyWithPreload(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyWithPreload(() => import("@/pages/ResetPassword"));
const AcceptInvite = lazyWithPreload(() => import("@/pages/AcceptInvite"));
const Dashboard = lazyWithPreload(() => import("@/pages/Dashboard"));
const ClientsPage = lazyWithPreload(() => import("@/pages/ClientsPage"));
const ScheduleTabs = lazyWithPreload(() => import("@/pages/ScheduleTabs"));
const JobDetailPage = lazyWithPreload(() => import("@/pages/JobDetailPage"));
const WeatherPage = lazyWithPreload(() => import("@/pages/WeatherPage"));
const InvoicesPage = lazyWithPreload(() => import("@/pages/InvoicesPage"));
const TimesheetsPage = lazyWithPreload(() => import("@/pages/TimesheetsPage"));
const ToBeInvoicedPage = lazyWithPreload(() => import("@/pages/ToBeInvoicedPage"));
const ToDoListPage = lazyWithPreload(() => import("@/pages/ToDoListPage"));
const PublicInvoice = lazyWithPreload(() => import("@/pages/PublicInvoice"));
const PublicJobPhotos = lazyWithPreload(() => import("@/pages/PublicJobPhotos"));
const PaymentReturn = lazyWithPreload(() => import("@/pages/PaymentReturn"));
const SettingsPage = lazyWithPreload(() => import("@/pages/SettingsPage"));
const SettingsSectionPage = lazyWithPreload(() => import("@/pages/settings/SettingsSectionPage"));
const ToolsPage = lazyWithPreload(() => import("@/pages/tools/ToolsPage"));
const MulchCalc = lazyWithPreload(() => import("@/pages/tools/MulchCalc"));
const StoneCalc = lazyWithPreload(() => import("@/pages/tools/StoneCalc"));
const SoilCalc = lazyWithPreload(() => import("@/pages/tools/SoilCalc"));
const PaversCalc = lazyWithPreload(() => import("@/pages/tools/PaversCalc"));
const TurfCalc = lazyWithPreload(() => import("@/pages/tools/TurfCalc"));
const FertiliserCalc = lazyWithPreload(() => import("@/pages/tools/FertiliserCalc"));
const GrassSeedCalc = lazyWithPreload(() => import("@/pages/tools/GrassSeedCalc"));
const SprayCalc = lazyWithPreload(() => import("@/pages/tools/SprayCalc"));

// Warm all route chunks in the background once the app is interactive, so the
// first tap on Schedule / Weather / etc. renders instantly. Highest-traffic
// views are ordered first. Runs during idle time to avoid competing with the
// initial critical load, and never blocks first paint.
const PRELOAD_ROUTES = [
  Dashboard, ScheduleTabs, WeatherPage, ClientsPage, InvoicesPage, JobDetailPage,
  TimesheetsPage, ToBeInvoicedPage, SettingsPage, SettingsSectionPage, ToolsPage,
  MulchCalc, StoneCalc, SoilCalc, PaversCalc, TurfCalc, FertiliserCalc, GrassSeedCalc, SprayCalc, ToDoListPage,
];

function App() {
  useEffect(() => {
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      PRELOAD_ROUTES.forEach((c) => { try { c.preload?.(); } catch { /* noop */ } });
    };
    const ric = window.requestIdleCallback;
    const handle = ric ? ric(warm, { timeout: 3000 }) : window.setTimeout(warm, 1500);
    return () => {
      cancelled = true;
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <ConfirmProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/invoice/:id" element={<PublicInvoice />} />
            <Route path="/photos/:token" element={<PublicJobPhotos />} />
            <Route path="/payment/success" element={<PaymentReturn />} />
            <Route path="/payment/cancel" element={<PaymentReturn />} />
            <Route
              element={
                <ProtectedRoute>
                  <APIProvider
                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                    libraries={["places", "marker"]}
                    region="AU"
                    language="en"
                  >
                    <AppLayout />
                  </APIProvider>
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Dashboard />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/jobs" element={<ScheduleTabs />} />
              <Route path="/jobs/:id" element={<JobDetailPage />} />
              <Route path="/schedule" element={<ScheduleTabs />} />
              <Route path="/weather" element={<WeatherPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/timesheets" element={<TimesheetsPage />} />
              <Route path="/to-be-invoiced" element={<ToBeInvoicedPage />} />
              <Route path="/todos" element={<ToDoListPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/:section" element={<SettingsSectionPage />} />
              <Route path="/tools" element={<ToolsPage />} />
              <Route path="/tools/mulch" element={<MulchCalc />} />
              <Route path="/tools/stone" element={<StoneCalc />} />
              <Route path="/tools/soil" element={<SoilCalc />} />
              <Route path="/tools/pavers" element={<PaversCalc />} />
              <Route path="/tools/turf" element={<TurfCalc />} />
              <Route path="/tools/fertiliser" element={<FertiliserCalc />} />
              <Route path="/tools/grass-seed" element={<GrassSeedCalc />} />
              <Route path="/tools/spray" element={<SprayCalc />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </ConfirmProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
